/**
 * Conversations API – seeker–gardener messaging
 */

import { apiGet, apiPost } from './api';

export interface ConversationMessage {
	id: number;
	conversation_id: number;
	sender_user_id: number;
	sender_name: string;
	sender_role?: string | null;
	body: string;
	read_at: string | null;
	created_at: string;
}

export interface Conversation {
	id: number;
	offer_id: number;
	project_id: number | null;
	plan_id?: number;
	created_at: string;
	updated_at: string;
	messages: ConversationMessage[];
}

/**
 * Get a single conversation by id (for notification deep links).
 */
export async function getConversationById(
	conversationId: number,
	userId: number
): Promise<Conversation> {
	const params = new URLSearchParams();
	params.set('user_id', String(userId));
	return apiGet<Conversation>(`/conversations/${conversationId}?${params.toString()}`);
}

/**
 * Get or create conversation for an offer. Returns conversation with last 50 messages.
 */
export async function getOrCreateConversationByOffer(
	offerId: number,
	userId: number
): Promise<Conversation> {
	const params = new URLSearchParams();
	params.set('offer_id', String(offerId));
	params.set('user_id', String(userId));
	return apiGet<Conversation>(`/conversations?${params.toString()}`);
}

/**
 * Get messages for a conversation (paginated).
 */
export async function getConversationMessages(
	conversationId: number,
	userId: number,
	opts?: { limit?: number }
): Promise<{ messages: ConversationMessage[] }> {
	const params = new URLSearchParams();
	params.set('user_id', String(userId));
	if (opts?.limit) params.set('limit', String(opts.limit));
	return apiGet<{ messages: ConversationMessage[] }>(
		`/conversations/${conversationId}/messages?${params.toString()}`
	);
}

/**
 * Send a message in a conversation.
 */
export async function sendConversationMessage(
	conversationId: number,
	userId: number,
	body: string
): Promise<ConversationMessage> {
	return apiPost<ConversationMessage>(
		`/conversations/${conversationId}/messages?user_id=${encodeURIComponent(userId)}`,
		{ body }
	);
}

/**
 * Get conversation for a project (same thread as the offer, after acceptance).
 */
export async function getProjectConversation(
	projectId: number,
	userId: number
): Promise<Conversation> {
	const params = new URLSearchParams();
	params.set('user_id', String(userId));
	return apiGet<Conversation>(`/projects/${projectId}/conversation?${params.toString()}`);
}
